// src/pages/NewBooking.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API_CONFIG, HOTELS, MEAL_PLANS } from '../App';
import { usePaymentPersons } from '../hooks/usePaymentPersons';
import { Container, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import './NewBooking.css';

function NewBooking() {
  const { loginUser, userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const { paymentPersons, loading: personsLoading } = usePaymentPersons();

  const [hotel, setHotel] = useState('1');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mealPlan, setMealPlan] = useState('EP');
  const [checkInDate, setCheckInDate] = useState('');
  const [days, setDays] = useState(1);
  const [numberOfPersons, setNumberOfPersons] = useState(1);
  const [specialRequest, setSpecialRequest] = useState('');
  
  const ALL_ROOM_TYPES = [
    { id: '4bed', label: '4 Bed', order: 1, value: 4 },
    { id: '5bed', label: '5 Bed', order: 2, value: 5 },
    { id: '6bed', label: '6 Bed', order: 3, value: 6 }
  ];

  const ROOM_COUNTS = [
    { value: 1, label: '1 Room' },
    { value: 2, label: '2 Rooms' },
    { value: 3, label: '3 Rooms' },
    { value: 4, label: '4 Rooms' },
    { value: 5, label: '5 Rooms' },
    { value: 6, label: '6 Rooms' },
    { value: 7, label: '7 Rooms' },
    { value: 8, label: '8 Rooms' },
    { value: 9, label: '9 Rooms' },
    { value: 10, label: '10 Rooms' }
  ];

  const [rooms, setRooms] = useState([
    { id: Date.now(), roomType: '4bed', numberOfRooms: 1 }
  ]);

  const [paymentPerson, setPaymentPerson] = useState(1);
  const [totalAmount, setTotalAmount] = useState('');
  const [tokenAmount, setTokenAmount] = useState('');
  const [balanceAmount, setBalanceAmount] = useState(0);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setCheckInDate(`${year}-${month}-${day}`);
  }, []);

  useEffect(() => {
    const total = parseFloat(totalAmount) || 0;
    const token = parseFloat(tokenAmount) || 0;
    setBalanceAmount(total - token);
  }, [totalAmount, tokenAmount]);

  // Get selected room types
  const selectedRoomTypes = rooms.map(room => room.roomType);

  // Get available room types (not selected yet)
  const getAvailableRoomTypes = () => {
    return ALL_ROOM_TYPES.filter(type => !selectedRoomTypes.includes(type.id));
  };

  // Check if all room types are selected
  const allRoomTypesSelected = selectedRoomTypes.length >= ALL_ROOM_TYPES.length;

  // Calculate total rooms
  const totalRooms = rooms.reduce((sum, room) => sum + parseInt(room.numberOfRooms || 1), 0);

  // Add Room
  const addRoom = () => {
    const availableTypes = getAvailableRoomTypes();
    if (availableTypes.length > 0) {
      const nextType = availableTypes[0];
      setRooms([...rooms, { 
        id: Date.now(), 
        roomType: nextType.id, 
        numberOfRooms: 1 
      }]);
    }
  };

  // Remove Room
  const removeRoom = (roomId) => {
    if (rooms.length > 1) {
      const newRooms = rooms.filter(room => room.id !== roomId);
      const sortedRooms = newRooms.sort((a, b) => {
        const orderA = ALL_ROOM_TYPES.find(t => t.id === a.roomType)?.order || 0;
        const orderB = ALL_ROOM_TYPES.find(t => t.id === b.roomType)?.order || 0;
        return orderA - orderB;
      });
      setRooms(sortedRooms);
    }
  };

  // Update Room Type
  const updateRoomType = (roomId, value) => {
    const isTypeSelected = rooms.some(room => room.id !== roomId && room.roomType === value);
    
    if (isTypeSelected) {
      alert('This room type is already selected. Please choose a different type.');
      return;
    }

    setRooms(rooms.map(room => {
      if (room.id === roomId) {
        return { ...room, roomType: value };
      }
      return room;
    }));
  };

  // Update Number of Rooms for a specific room type
  const updateRoomCount = (roomId, value) => {
    setRooms(rooms.map(room => {
      if (room.id === roomId) {
        return { ...room, numberOfRooms: parseInt(value) };
      }
      return room;
    }));
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[0-9]{10}$/.test(phone);

  const validateField = (field, value) => {
    let error = '';
    switch (field) {
      case 'hotel':
        if (!value) error = 'Please select a hotel';
        break;
      case 'userName':
        if (!value.trim()) error = 'Guest name is required';
        else if (value.trim().length < 2) error = 'Name must be at least 2 characters';
        break;
      case 'email':
        if (!value) error = 'Email is required';
        else if (!validateEmail(value)) error = 'Please enter a valid email';
        break;
      case 'phone':
        if (!value) error = 'Phone is required';
        else if (!validatePhone(value)) error = 'Enter a valid 10-digit phone';
        break;
      case 'checkInDate':
        if (!value) error = 'Check-in date is required';
        break;
      case 'numberOfPersons':
        if (!value || parseInt(value) < 1) error = 'At least 1 person required';
        break;
      case 'totalAmount':
        if (!value || parseFloat(value) < 0) error = 'Please enter a valid amount';
        break;
      case 'tokenAmount':
        if (value && parseFloat(value) > parseFloat(totalAmount || 0)) 
          error = 'Token amount cannot exceed total';
        break;
      default: break;
    }
    return error;
  };

  const handleFieldChange = (field, value) => {
    switch (field) {
      case 'hotel': setHotel(value); break;
      case 'userName': setUserName(value); break;
      case 'email': setEmail(value); break;
      case 'phone': setPhone(value.replace(/\D/g, '')); return;
      case 'checkInDate': setCheckInDate(value); break;
      case 'days': setDays(value); break;
      case 'numberOfPersons': setNumberOfPersons(value); break;
      case 'totalAmount': setTotalAmount(value); break;
      case 'tokenAmount': setTokenAmount(value); break;
      case 'paymentPerson': setPaymentPerson(value); break;
      default: break;
    }
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const validateForm = () => {
    const fields = ['hotel', 'userName', 'email', 'phone', 'checkInDate', 'numberOfPersons', 'totalAmount'];
    const newErrors = {};
    let hasError = false;
    
    fields.forEach(field => {
      let value;
      switch (field) {
        case 'hotel': value = hotel; break;
        case 'userName': value = userName; break;
        case 'email': value = email; break;
        case 'phone': value = phone; break;
        case 'checkInDate': value = checkInDate; break;
        case 'numberOfPersons': value = numberOfPersons; break;
        case 'totalAmount': value = totalAmount; break;
        default: value = '';
      }
      const error = validateField(field, value);
      newErrors[field] = error;
      if (error) hasError = true;
    });
    
    if (tokenAmount && parseFloat(tokenAmount) > parseFloat(totalAmount || 0)) {
      newErrors.tokenAmount = 'Token amount cannot exceed total';
      hasError = true;
    }

    setErrors(newErrors);
    const allTouched = {};
    fields.forEach(field => { allTouched[field] = true; });
    setTouched(allTouched);
    
    return !hasError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validateForm()) return;

    setIsSubmitting(true);

    const selectedPerson = paymentPersons.find(p => p.id === parseInt(paymentPerson));
    const paymentPersonName = selectedPerson?.name || 'Unknown';

    // Build room details for API - Send numeric value instead of id
    const roomDetails = rooms.map((room) => {
      const roomTypeObj = ALL_ROOM_TYPES.find(t => t.id === room.roomType);
      return {
        roomType: roomTypeObj ? roomTypeObj.value : 4, // Send numeric value (4, 5, 6)
        count: parseInt(room.numberOfRooms) || 1
      };
    });

    const parsedUserId = userId ? parseInt(userId) : null;

    const bookingData = {
      hotelId: parseInt(hotel),
      hotelName: HOTELS.find(h => h.id === parseInt(hotel))?.name || '',
      username: userName,
      email: email,
      phone: phone,
      mealPlan: mealPlan,
      checkInDate: checkInDate,
      checkInTime: null,
      numberOfDays: parseInt(days),
      numberOfRooms: totalRooms,
      numberOfPersons: parseInt(numberOfPersons),
      rooms: roomDetails,
      specialRequest: specialRequest || 'None',
      bookingReference: `BK-${Date.now().toString().slice(-8)}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
      paymentPerson: parseInt(paymentPerson),
      paymentPersonName: paymentPersonName,
      totalAmount: parseFloat(totalAmount) || 0,
      tokenAmount: parseFloat(tokenAmount) || 0,
      balanceAmount: balanceAmount,
      status: 'confirmed',
      userId: parsedUserId,
      createdBy: loginUser
    };

    console.log('Booking Data:', JSON.stringify(bookingData, null, 2));

    try {
      const response = await fetch(API_CONFIG.BOOKING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(bookingData)
      });

      const responseData = await response.json();
      
      if (response.ok && responseData.status === 'success') {
        alert('✅ Booking Confirmed successfully!');
        resetForm();
        navigate('/all-bookings');
      } else {
        throw new Error(responseData.message || 'Submission failed');
      }
    } catch (error) {
      setSubmitError(error.message || 'Booking confirmation failed!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setHotel('1');
    setUserName('');
    setEmail('');
    setPhone('');
    setCheckInDate(new Date().toISOString().split('T')[0]);
    setDays(1);
    setNumberOfPersons(1);
    setSpecialRequest('');
    setTotalAmount('');
    setTokenAmount('');
    setBalanceAmount(0);
    setPaymentPerson(1);
    setRooms([{ id: Date.now(), roomType: '4bed', numberOfRooms: 1 }]);
    setErrors({});
    setTouched({});
    setSubmitError('');
  };

  const selectedPerson = paymentPersons.find(p => p.id === parseInt(paymentPerson));

  const getAvailableTypesForRoom = (currentRoomId) => {
    const selectedOtherTypes = rooms
      .filter(room => room.id !== currentRoomId)
      .map(room => room.roomType);
    
    return ALL_ROOM_TYPES.filter(type => !selectedOtherTypes.includes(type.id));
  };

  return (
    <Container fluid className="new-booking-page py-3">
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold text-primary-dark">📝 New Booking</h2>
        </Col>
      </Row>

      {submitError && (
        <Row>
          <Col>
            <div className="error-alert">{submitError}</div>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit} className="booking-form">
        <Row className="g-3">
          {/* Hotel */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Hotel <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={hotel}
                onChange={(e) => handleFieldChange('hotel', e.target.value)}
                onBlur={() => handleBlur('hotel')}
                isInvalid={touched.hotel && errors.hotel}
                className="rounded-3"
              >
                <option value="">Select Hotel</option>
                {HOTELS.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.hotel}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Guest Name */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Guest Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={userName}
                onChange={(e) => handleFieldChange('userName', e.target.value)}
                onBlur={() => handleBlur('userName')}
                placeholder="Full name"
                isInvalid={touched.userName && errors.userName}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.userName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Email */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="email@example.com"
                isInvalid={touched.email && errors.email}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Phone */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Phone <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="tel"
                value={phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setPhone(value);
                  const error = validateField('phone', value);
                  setErrors(prev => ({ ...prev, phone: error }));
                }}
                onBlur={() => handleBlur('phone')}
                placeholder="9999999999"
                maxLength="10"
                isInvalid={touched.phone && errors.phone}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Meal Plan - Full Width */}
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="fw-semibold">Meal Plan</Form.Label>
              <div className="meal-options">
                {Object.entries(MEAL_PLANS).map(([key, plan]) => (
                  <label key={key} className={`meal-option ${mealPlan === key ? 'active' : ''}`}>
                    <input 
                      type="radio" 
                      name="mealPlan" 
                      value={key} 
                      checked={mealPlan === key} 
                      onChange={(e) => setMealPlan(e.target.value)}
                    />
                    <span>{plan.emoji} {plan.label}</span>
                  </label>
                ))}
              </div>
            </Form.Group>
          </Col>

          {/* Check-in */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Check-in <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={checkInDate}
                onChange={(e) => handleFieldChange('checkInDate', e.target.value)}
                onBlur={() => handleBlur('checkInDate')}
                min={new Date().toISOString().split('T')[0]}
                isInvalid={touched.checkInDate && errors.checkInDate}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.checkInDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Days */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Days</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="30"
                value={days}
                onChange={(e) => handleFieldChange('days', e.target.value)}
                className="rounded-3"
              />
            </Form.Group>
          </Col>

          {/* Number of Persons */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Number of Persons <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="100"
                value={numberOfPersons}
                onChange={(e) => handleFieldChange('numberOfPersons', e.target.value)}
                onBlur={() => handleBlur('numberOfPersons')}
                placeholder="Number of persons"
                isInvalid={touched.numberOfPersons && errors.numberOfPersons}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.numberOfPersons}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Payment Person */}
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold">Payment Person <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={paymentPerson}
                onChange={(e) => handleFieldChange('paymentPerson', parseInt(e.target.value))}
                className="rounded-3"
              >
                <option value="">Select Payment Person</option>
                {personsLoading ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  paymentPersons.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))
                )}
              </Form.Select>
              {selectedPerson && (
                <Form.Text className="text-muted">Selected: {selectedPerson.name}</Form.Text>
              )}
            </Form.Group>
          </Col>

          {/* Rooms Section - Full Width */}
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="fw-semibold">Room Details</Form.Label>
              <div className="rooms-section">
                <div className="rooms-summary">
                  <Badge bg="primary" className="rounded-pill px-3 py-2">
                    Total Rooms: {totalRooms}
                  </Badge>
                  <Badge bg="info" className="rounded-pill px-3 py-2">
                    Selected Room Types: {rooms.length}/{ALL_ROOM_TYPES.length}
                  </Badge>
                </div>
                
                {rooms.map((room, index) => {
                  const availableTypes = getAvailableTypesForRoom(room.id);
                  
                  return (
                    <div key={room.id} className="room-row">
                      <span className="room-number">Room Type {index + 1}</span>
                      
                      {/* Room Type Select */}
                      <Form.Select
                        value={room.roomType}
                        onChange={(e) => updateRoomType(room.id, e.target.value)}
                        className="room-type-select rounded-3"
                      >
                        {ALL_ROOM_TYPES.map(type => (
                          <option 
                            key={type.id} 
                            value={type.id}
                            disabled={!availableTypes.some(t => t.id === type.id) && type.id !== room.roomType}
                          >
                            {type.label} {!availableTypes.some(t => t.id === type.id) && type.id !== room.roomType ? '(Already Selected)' : ''}
                          </option>
                        ))}
                      </Form.Select>
                      
                      {/* Number of Rooms Select */}
                      <Form.Select
                        value={room.numberOfRooms}
                        onChange={(e) => updateRoomCount(room.id, e.target.value)}
                        className="room-count-select rounded-3"
                      >
                        {ROOM_COUNTS.map(count => (
                          <option key={count.value} value={count.value}>{count.label}</option>
                        ))}
                      </Form.Select>
                      
                      {rooms.length > 1 && (
                        <Button
                          variant="danger"
                          size="sm"
                          className="btn-remove rounded-3"
                          onClick={() => removeRoom(room.id)}
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  );
                })}
                
                {/* Add Room Button */}
                {!allRoomTypesSelected && (
                  <Button
                    variant="primary"
                    className="btn-add-room rounded-3"
                    onClick={addRoom}
                  >
                    + Add Room
                  </Button>
                )}
                
                {allRoomTypesSelected && (
                  <div className="all-types-selected-message">
                    ✅ All room types have been selected
                  </div>
                )}
              </div>
            </Form.Group>
          </Col>

          {/* Total Amount */}
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold">Total Amount <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                value={totalAmount}
                onChange={(e) => handleFieldChange('totalAmount', e.target.value)}
                onBlur={() => handleBlur('totalAmount')}
                placeholder="0"
                isInvalid={touched.totalAmount && errors.totalAmount}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.totalAmount}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Token Amount */}
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold">Token Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                value={tokenAmount}
                onChange={(e) => handleFieldChange('tokenAmount', e.target.value)}
                onBlur={() => handleBlur('tokenAmount')}
                placeholder="0"
                isInvalid={touched.tokenAmount && errors.tokenAmount}
                className="rounded-3"
              />
              <Form.Control.Feedback type="invalid">
                {errors.tokenAmount}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Balance Amount */}
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold">Balance Amount</Form.Label>
              <Form.Control
                type="text"
                value={balanceAmount}
                readOnly
                className="rounded-3 bg-light fw-bold"
              />
            </Form.Group>
          </Col>

          {/* Special Request - Full Width */}
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="fw-semibold">Special Request</Form.Label>
              <Form.Control
                as="textarea"
                rows="2"
                value={specialRequest}
                onChange={(e) => setSpecialRequest(e.target.value)}
                placeholder="Any special requests..."
                className="rounded-3"
              />
            </Form.Group>
          </Col>

          {/* Submit Button - Full Width */}
          <Col xs={12}>
            <Button
              type="submit"
              className="w-100 py-3 fw-bold rounded-3 btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Book Now'}
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default NewBooking;